import axios from 'axios';

const api = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL || ''}/api`,
  headers: { 'Content-Type': 'application/json' },
});

// Inietta JWT su ogni richiesta
// access_token in sessionStorage (non persiste tra sessioni — riduce superficie XSS)
// refresh_token in localStorage (persiste per il rinnovo automatico)
api.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  return config;
});

// Endpoint dove un 401 significa "input errato", NON "sessione scaduta":
// non devono passare dal ramo auto-refresh. Senza esclusione, il 401 di
// /auth/login/ (credenziali errate) finiva nel catch del refresh → wipe
// storage + window.location.href='/auth/login' → reload che azzerava lo
// state React PRIMA del messaggio d'errore (DEBT-013). /auth/refresh/ incluso
// per difesa: oggi non passa da questa istanza, ma se un domani lo facesse non
// deve ricorsivare nel proprio ramo.
const AUTH_NO_REFRESH = ['/auth/login/', '/auth/refresh/'];

// DEBT-015 — single-flight refresh. Una sola POST /auth/refresh/ in volo per volta:
// le richieste che ricevono 401 mentre un refresh è già in corso attendono la STESSA
// promise (module-scope) invece di lanciarne una propria. Necessario perché
// ROTATE_REFRESH_TOKENS + BLACKLIST_AFTER_ROTATION (settings.py): N refresh concorrenti
// → il primo ruota e blacklista il refresh token, gli altri usano un token morto → 401
// → logout spurio. La promise si resetta SEMPRE a fine refresh (vedi .finally).
let refreshPromise = null;

// Logout centralizzato ed eseguito UNA volta sola: se più richieste in attesa falliscono
// insieme, solo la prima esegue wipe+redirect; le altre propagano solo il reject. Il flag
// non si resetta: forceLogout naviga via → la pagina si ricarica e lo stato modulo riparte.
let loggingOut = false;
function forceLogout() {
  if (loggingOut) return;
  loggingOut = true;
  sessionStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  window.location.href = '/auth/login';
}

// DEBT-016 — con la rotation ogni /auth/refresh/ restituisce un NUOVO refresh token e
// blacklista il vecchio: va persistito, altrimenti il ciclo di refresh successivo usa il
// token blacklistato → logout deterministico (~ogni 2h, dopo il 2° giro di refresh) per
// tutti gli utenti. Persistiamo SIA access SIA refresh; risolve con il nuovo access token.
function eseguiRefresh() {
  const refresh = localStorage.getItem('refresh_token');
  return axios
    .post(`${process.env.REACT_APP_API_URL || ''}/api/auth/refresh/`, { refresh })
    .then(({ data }) => {
      sessionStorage.setItem('access_token', data.access);
      if (data.refresh) localStorage.setItem('refresh_token', data.refresh);
      return data.access;
    });
}

// Auto-refresh token se scaduto
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const skipRefresh = AUTH_NO_REFRESH.some((u) => (original?.url || '').includes(u));
    if (error.response?.status === 401 && !original._retry && !skipRefresh) {
      original._retry = true;
      try {
        // La prima richiesta crea la promise condivisa; le concorrenti la riusano.
        // Reset a null nel finally → un 401 successivo può rinegoziare un nuovo refresh.
        if (!refreshPromise) {
          refreshPromise = eseguiRefresh().finally(() => { refreshPromise = null; });
        }
        const newAccess = await refreshPromise;
        original.headers.Authorization = `Bearer ${newAccess}`;
        return api(original);
      } catch {
        // Refresh fallito (token scaduto/blacklistato) — logout una volta sola
        forceLogout();
        return Promise.reject(error);
      }
    }
    // Le pagine che gestiscono l'errore in-line (es. /integrazioni, riquadro
    // dedicato) passano `skipGlobalError: true` per non far comparire ANCHE il
    // toast globale: un errore già spiegato non va raddoppiato.
    if ((error.response?.status >= 500 || !error.response) && !original?.skipGlobalError) {
      window.dispatchEvent(new CustomEvent('api-error', {
        detail: {
          message: error.response
            ? 'Errore del server. Riprova tra qualche secondo.'
            : 'Connessione persa. Controlla la rete.',
          status: error.response?.status,
        },
      }));
    }
    return Promise.reject(error);
  }
);

export default api;

// ── Endpoint helpers ─────────────────────────────────────────────

export const auth = {
  login: (email, password) => api.post('/auth/login/', { email, password }),
  logout: (refresh) => api.post('/auth/logout/', { refresh }),
  socialLogin: (provider, code, redirect_uri, code_verifier) => api.post(`/auth/social/${provider}/`, { code, redirect_uri, ...(code_verifier && { code_verifier }) }),
  register: (data) => api.post('/auth/register/', data),
  // Cantiere Registrazione, Fetta 3 (DEC-056): invia il codice SMS di verifica
  // del telefono. `data` = { registration_ticket, telefono }. Prerequisito di
  // registerComplete, che ora esige anche `otp_code`.
  registerSendOtp: (data) => api.post('/auth/register/send-otp/', data),
  // Cantiere Registrazione, Fetta 2b (DEC-064): completa la registrazione
  // self-service dal form di adesione. `data` porta il biglietto firmato
  // (registration_ticket) + i dati aziendali + `otp_code`. L'email NON viaggia
  // qui: il backend la legge dal biglietto (Regola 62).
  registerComplete: (data) => api.post('/auth/register/complete/', data),
  // Cantiere Registrazione, Fetta 5c (DEC-067): registrazione via email/password.
  // `start` avvia (email+password → invia link di verifica); `verify` valida il
  // token del link e restituisce il biglietto (provider='email') con cui il form
  // di adesione prosegue. La password NON viaggia nel link: è hashata lato server.
  registerEmailStart: (data) => api.post('/auth/register/email/start/', data),
  registerEmailVerify: (token) => api.post('/auth/register/email/verify/', { token }),
  // CAP-024 (DEC-176): l'app sa PRIMA se le iscrizioni sono chiuse, e in quel caso
  // raccoglie solo un contatto per la lista d'attesa (mai una password).
  statoIscrizioni: () => api.get('/auth/iscrizioni/'),
  listaAttesa: (data) => api.post('/auth/lista-attesa/', data),
  me: () => api.get('/auth/me/'),
  updateMe: (data) => api.patch('/auth/me/', data),
  // SEC-051-profilo (DEC-069): cambio numero WhatsApp VERIFICATO via SMS.
  // `send-otp` manda il codice al nuovo numero; `confirm` verifica e lo associa.
  // Il numero NON è più modificabile via updateMe (prova di possesso richiesta).
  mePhoneSendOtp: (telefono) => api.post('/auth/me/phone/send-otp/', { telefono }),
  mePhoneConfirm: (data) => api.post('/auth/me/phone/confirm/', data),
  features: () => api.get('/me/features/'),
  auditLog: () => api.get('/auth/audit-log/'),
  changePassword: (data) => api.post('/auth/change-password/', data),
  gdprExport: () => api.get('/auth/gdpr/export/'),
  gdprDelete: (data) => api.post('/auth/gdpr/delete/', data),
  // Reset password self-service (Fetta 5b): richiesta link + conferma nuova password
  passwordResetRequest: (email) => api.post('/auth/password-reset/', { email }),
  passwordResetConfirm: (data) => api.post('/auth/password-reset/confirm/', data),
  // MFA Magic Link
  sendMagicLink: (email) => api.post('/auth/magic-link/send/', { email }),
  verifyMagicLink: (token) => api.post('/auth/verify-magic-link/', { token }),
  toggleMfa: (mfa_enabled) => api.patch('/auth/mfa/', { mfa_enabled }),
  // AIstotele-SEC-001 — scambia un UUID one-shot col JWT (nel body). axios "nudo":
  // il tab non e' ancora autenticato (nessun auth header da iniettare) e un 404
  // non deve attivare l'interceptor di redirect-login.
  exchangeImpersonation: (uuid) =>
    axios.post(`${process.env.REACT_APP_API_URL || ''}/api/auth/exchange/${uuid}/`),
  // Sessioni
  sessions: () => api.get('/auth/sessions/'),
  logoutAll: () => api.post('/auth/logout-all/'),
  // Tenant
  getTenant: () => api.get('/auth/tenant/'),
  updateTenant: (data) => api.patch('/auth/tenant/', data),
  updateTenantLogo: (file) => {
    const fd = new FormData();
    fd.append('logo', file);
    return api.patch('/auth/tenant/logo/', fd);
  },
  updateTenantFirma: (file) => {
    const fd = new FormData();
    fd.append('firma', file);
    return api.patch('/auth/tenant/firma/', fd);
  },
  testSmtp: (data) => api.post('/auth/tenant/test-smtp/', data),
};

// Integrazioni — connettori servizi esterni (Fase 1: Fatture in Cloud)
export const integrazioni = {
  // skipGlobalError: la pagina /integrazioni mostra l'errore nel proprio
  // riquadro → nessun toast globale duplicato (503/5xx compresi).
  // Il catalogo arriva dal backend, non è cablato qui: la disponibilità dipende
  // dai feature flag del tenant, e due elenchi divergerebbero (uno mostrerebbe
  // «Collega» dove l'altro risponde 403).
  catalogo: () => api.get('/integrazioni/catalogo/', { skipGlobalError: true }),
  connessioni: () => api.get('/integrazioni/connessioni/', { skipGlobalError: true }),
  ficStart: () => api.post('/integrazioni/fic/oauth/start/', null, { skipGlobalError: true }),
  ficDisconnect: () => api.post('/integrazioni/fic/disconnect/', null, { skipGlobalError: true }),
  ficClienti: () => api.get('/integrazioni/fic/clienti/', { skipGlobalError: true }),
  // Emissione del documento su Fatture in Cloud. `skipGlobalError`: gli esiti
  // non-200 QUI non sono guasti da toast generico — sono i quattro esiti che
  // la schermata deve distinguere (`ficEsiti.js`), e uno di essi vieta il
  // riprova. Un toast globale li schiaccerebbe tutti in «errore».
  ficEmetti: (fatturaId) =>
    api.post(`/integrazioni/fic/fatture/${fatturaId}/crea/`, null, { skipGlobalError: true }),
};

export const cantieri = {
  // CRUD cantiere
  list: (params) => api.get('/cantieri/', { params }),
  get: (id) => api.get(`/cantieri/${id}/`),
  create: (data) => api.post('/cantieri/', data),
  update: (id, data) => api.patch(`/cantieri/${id}/`, data),
  delete: (id) => api.delete(`/cantieri/${id}/`),
  deletePreview: (id) => api.get(`/cantieri/${id}/delete-preview/`),
  statistiche: () => api.get('/cantieri/statistiche/'),
  cambiaStato: (id, stato) => api.post(`/cantieri/${id}/cambia_stato/`, { stato }),
  // Eventi cantiere
  eventi: (id) => api.get(`/cantieri/${id}/eventi/`),
  addEvento: (id, data, file) => {
    if (file) {
      const fd = new FormData();
      Object.entries(data).forEach(([k, v]) => v != null && fd.append(k, typeof v === 'object' ? JSON.stringify(v) : v));
      fd.append('allegato', file);
      return api.post(`/cantieri/${id}/eventi/`, fd);
    }
    return api.post(`/cantieri/${id}/eventi/`, data);
  },
  patchEvento: (cantiereId, eventoId, data) => api.patch(`/cantieri/${cantiereId}/eventi/${eventoId}/`, data),
  deleteEvento: (cantiereId, eventoId) => api.delete(`/cantieri/${cantiereId}/eventi/${eventoId}/`),
  // Report giornaliero
  reports: (cantiereId) => api.get(`/cantieri/${cantiereId}/report/`),
  createReport: (cantiereId, data) => api.post(`/cantieri/${cantiereId}/report/`, data),
  deleteReport: (cantiereId, reportId) => api.delete(`/cantieri/${cantiereId}/report/${reportId}/`),
  updateReport: (cantiereId, reportId, data) => api.patch(`/cantieri/${cantiereId}/report/${reportId}/`, data),
  // Dashboard hub
  dashboard: (cantiereId) => api.get(`/cantieri/${cantiereId}/dashboard/`),
  // Piano lavori
  getPiano: (cantiereId) => api.get(`/cantieri/${cantiereId}/piano/`),
  createPiano: (cantiereId, data) => api.post(`/cantieri/${cantiereId}/piano/`, data),
  updatePiano: (cantiereId, data) => api.patch(`/cantieri/${cantiereId}/piano/`, data),
  createFase: (cantiereId, data) => api.post(`/cantieri/${cantiereId}/piano/fasi/`, data),
  updateFase: (cantiereId, faseId, data) => api.patch(`/cantieri/${cantiereId}/piano/fasi/${faseId}/`, data),
  deleteFase: (cantiereId, faseId) => api.delete(`/cantieri/${cantiereId}/piano/fasi/${faseId}/`),
  createTask: (cantiereId, faseId, data) => api.post(`/cantieri/${cantiereId}/piano/fasi/${faseId}/tasks/`, data),
  completaTask: (cantiereId, taskId) => api.post(`/cantieri/${cantiereId}/piano/tasks/${taskId}/completa/`),
  createMilestone: (cantiereId, data) => api.post(`/cantieri/${cantiereId}/piano/milestone/`, data),
  updateMilestone: (cantiereId, milestoneId, data) => api.patch(`/cantieri/${cantiereId}/piano/milestone/${milestoneId}/`, data),
  deleteMilestone: (cantiereId, milestoneId) => api.delete(`/cantieri/${cantiereId}/piano/milestone/${milestoneId}/`),
  // R3 — conferma una proposta fattura da milestone → emette la fattura bozza (idempotente).
  confermaPropostaFattura: (cantiereId, propostaId) => api.post(`/cantieri/${cantiereId}/piano/proposte-fattura/${propostaId}/conferma/`),
  importaContratto: (cantiereId, fileOrText) => {
    if (fileOrText instanceof File) {
      const fd = new FormData(); fd.append('file', fileOrText);
      return api.post(`/cantieri/${cantiereId}/piano/importa-contratto/`, fd, { timeout: 60000 });
    }
    return api.post(`/cantieri/${cantiereId}/piano/importa-contratto/`, { testo: fileOrText }, { timeout: 60000 });
  },
};

export const clienti = {
  list: (params) => api.get('/clienti/', { params }),
  get: (id) => api.get(`/clienti/${id}/`),
  create: (data) => api.post('/clienti/', data),
  update: (id, data) => api.patch(`/clienti/${id}/`, data),
  delete: (id) => api.delete(`/clienti/${id}/`),
  importaPdf: (file) => { const fd = new FormData(); fd.append('file', file); return api.post('/clienti/importa-pdf/', fd); },
  // M23 — lifecycle endpoints
  lifecycle: (id) => api.get(`/clienti/${id}/lifecycle/`),
  transizione: (id, payload) => api.post(`/clienti/${id}/lifecycle/transizione/`, payload),
  // M23 S2 — funnel commerciale per Governance sezione 06
  funnel: () => api.get('/clienti/funnel/'),
};

export const fatture = {
  list: (params) => api.get('/fatture/', { params }),
  get: (id) => api.get(`/fatture/${id}/`),
  create: (data) => api.post('/fatture/', data),
  update: (id, data) => api.patch(`/fatture/${id}/`, data),
  delete: (id) => api.delete(`/fatture/${id}/`),
  pdf: (id) => api.get(`/fatture/${id}/pdf/`, { responseType: 'blob' }),
  pdfSalva: (id) => api.post(`/fatture/${id}/pdf/salva/`),
  inviaCommercialista: (fatturaIds) => api.post('/fatture/invia-commercialista/', { fattura_ids: fatturaIds }),
  inviaCliente: (id, payload) => api.post(`/fatture/${id}/invia-cliente/`, payload),
  inviaSollecito: (id, payload) => api.post(`/fatture/${id}/invia-sollecito/`, payload),
  inviaClienti: (payload) => api.post('/fatture/invia-clienti/', payload),
  kpi: (params) => api.get('/fatture/kpi/', { params }),
  kpiMensile: (params) => api.get('/fatture/kpi/mensile/', { params }),
  governanceKpi: (params) => api.get('/fatture/governance/kpi/', { params }),
  invia: (id) => api.post(`/fatture/${id}/invia/`),
  segnaPagata: (id, data) => api.post(`/fatture/${id}/segna_pagata/`, data || {}),
  annulla: (id) => api.post(`/fatture/${id}/annulla/`),
};

export const preventivi = {
  list: (params) => api.get('/preventivi/', { params }),
  get: (id) => api.get(`/preventivi/${id}/`),
  create: (data) => api.post('/preventivi/', data),
  update: (id, data) => api.patch(`/preventivi/${id}/`, data),
  delete: (id) => api.delete(`/preventivi/${id}/`),
  kpi: (params) => api.get('/preventivi/kpi/', { params }),
  invia: (id) => api.post(`/preventivi/${id}/invia/`),
  accetta: (id) => api.post(`/preventivi/${id}/accetta/`),
  rifiuta: (id) => api.post(`/preventivi/${id}/rifiuta/`),
  addRiga: (id, data) => api.post(`/preventivi/${id}/righe/`, data),
  updateRiga: (id, rigaId, data) => api.put(`/preventivi/${id}/righe/${rigaId}/`, data),
  deleteRiga: (id, rigaId) => api.delete(`/preventivi/${id}/righe/${rigaId}/`),
  // R1 — scadenziario pagamenti (nested, tenant-scoped)
  getPagamenti: (id) => api.get(`/preventivi/${id}/pagamenti/`).then((r) => r.data),
  addPagamento: (id, data) => api.post(`/preventivi/${id}/pagamenti/`, data),
  updatePagamento: (id, rigaId, data) => api.put(`/preventivi/${id}/pagamenti/${rigaId}/`, data),
  deletePagamento: (id, rigaId) => api.delete(`/preventivi/${id}/pagamenti/${rigaId}/`),
  getPdfData: (id) => api.get(`/preventivi/${id}/pdf-data/`).then(r => r.data),
  importaPdf: (file) => { const fd = new FormData(); fd.append('file', file); return api.post('/preventivi/importa-pdf/', fd); },
  apriCantiere: (id, payload) => api.post(`/preventivi/${id}/apri-cantiere/`, payload || {}),
  inviaCliente: (id, payload) => api.post(`/preventivi/${id}/invia-cliente/`, payload),
  inviaClienti: (payload) => api.post('/preventivi/invia-clienti/', payload),
};

export const fornitori = {
  list: (params) => api.get('/fornitori/', { params }),
  get: (id) => api.get(`/fornitori/${id}/`),
  create: (data) => api.post('/fornitori/', data),
  update: (id, data) => api.patch(`/fornitori/${id}/`, data),
  delete: (id) => api.delete(`/fornitori/${id}/`),
};

// R8-MAN — listino prezzi (CRUD su /api/listino/, modello VoceListino di R8-M).
export const listino = {
  list: (params) => api.get('/listino/', { params }),
  get: (id) => api.get(`/listino/${id}/`),
  create: (data) => api.post('/listino/', data),
  update: (id, data) => api.patch(`/listino/${id}/`, data),
  delete: (id) => api.delete(`/listino/${id}/`),
  // R8-MAN-MULTI — eliminazione batch in UNA chiamata: { ids:[...] }, al massimo 1000.
  // La modalita' { tutte:true } NON esiste piu' (FE-085, DEC-111): il server risponde 400.
  eliminaBatch: (payload) => api.post('/listino/elimina-batch/', payload),
  // FE-085 — «Seleziona tutte le N»: il server conta le voci del FILTRO (params = stessi
  // search/categoria_ref della lista) e ne da' l'impronta; l'eliminazione la ripresenta
  // e riceve 409 { conteggio, impronta } se nel frattempo l'insieme e' cambiato.
  eliminaFiltrateAnteprima: (params) => api.post('/listino/elimina-filtrate/anteprima/', {}, { params }),
  eliminaFiltrate: (params, impronta) => api.post('/listino/elimina-filtrate/', { impronta }, { params }),
  // R8-IMP-ASYNC — avvia l'import da PDF: risponde 202 con { id, stato, totale_righe }.
  // L'elaborazione (minuti su cataloghi grandi) gira in background; si fa polling con importStato.
  importaPdf: (file) => { const fd = new FormData(); fd.append('file', file); return api.post('/listino/importa-pdf/', fd); },
  importStato: (jobId) => api.get(`/listino/import-stato/${jobId}/`),
  // R8-IMP-DEDUP — dedup: unione voci, archiviazione badge, storico import.
  unisci: (tieni, rimuovi) => api.post('/listino/unisci/', { tieni, rimuovi }),
  archiviaBadge: (id) => api.post(`/listino/${id}/archivia-badge/`),
  importStorico: () => api.get('/listino/import-storico/'),
  // R8-DEDUP-MANUALE — chiedi PRIMA di salvare: { caso, voce } (identica/contenuta/diversa).
  verificaDuplicato: (payload) => api.post('/listino/verifica-duplicato/', payload),
  // R8-CAT — categorie del listino (entità): lista con voci_count, crea, rinomina/colore, elimina.
  // Il filtro delle voci per categoria usa list({ categoria_ref: <id> | 'none' }).
  categorie: (params) => api.get('/listino/categorie/', { params }),
  creaCategoria: (data) => api.post('/listino/categorie/', data),
  aggiornaCategoria: (id, data) => api.patch(`/listino/categorie/${id}/`, data),
  eliminaCategoria: (id) => api.delete(`/listino/categorie/${id}/`),
  eliminaCategorieBatch: (ids) => api.post('/listino/categorie/elimina-batch/', { ids }),
};

export const ordini = {
  list: (params) => api.get('/fornitori/ordini/', { params }),
  get: (id) => api.get(`/fornitori/ordini/${id}/`),
  create: (data) => api.post('/fornitori/ordini/', data),
  update: (id, data) => api.patch(`/fornitori/ordini/${id}/`, data),
  delete: (id) => api.delete(`/fornitori/ordini/${id}/`),
  importaPdf: (file) => { const fd = new FormData(); fd.append('file', file); return api.post('/fornitori/ordini/importa-pdf/', fd); },
};

export const documenti = {
  list:       (params) => api.get('/documenti/', { params }),
  upload:     (formData) => api.post('/documenti/', formData),
  delete:     (id) => api.delete('/documenti/' + id + '/'),
  // FE-074 — download autenticato (blob): usato quando il documento non ha un
  // file_url diretto ma e' generabile al volo (fattura/preventivo senza PDF).
  download:   (id) => api.get('/documenti/' + id + '/download/', { responseType: 'blob' }),
  share:      (id, scadenza_giorni) => api.post('/documenti/' + id + '/share/', { scadenza_giorni }),
  shareStats: (id) => api.get('/documenti/' + id + '/share/stats/'),
  // Condivisione M3 — endpoint autenticati
  condividi:     (id, data) => api.post('/documenti/' + id + '/condividi/', data),
  condivisioni:  (id) => api.get('/documenti/' + id + '/condivisioni/'),
  revocaLink:    (token) => api.post('/documenti/condivisione/' + token + '/revoca/', {}),
  statistiche: () => api.get('/documenti/statistiche/'),
  // Cartelle
  getCartelle:          () => api.get('/documenti/cartelle/'),
  creaCartella:         (data) => api.post('/documenti/cartelle/', data),
  eliminaCartella:      (id) => api.delete('/documenti/cartelle/' + id + '/'),
  patchCartella:        function(id, data) { return api.patch('/documenti/cartelle/' + id + '/', data); },
  // Tag
  getTag:      () => api.get('/documenti/tag/'),
  creaTag:     (data) => api.post('/documenti/tag/', data),
  eliminaTag:  (id) => api.delete('/documenti/tag/' + id + '/'),
  // Preferiti / Condivisi / Recenti
  togglePreferito: function(id) { return api.post('/documenti/' + id + '/preferito/'); },
};

// Condivisione M3 — endpoint pubblici (senza JWT interceptor)
const BASE_URL = (process.env.REACT_APP_API_URL || '') + '/api';

export const condivisionePublic = {
  verifica:    (token) => axios.get(BASE_URL + '/documenti/condivisione/' + token + '/'),
  verificaPin: (token, pin) => axios.post(BASE_URL + '/documenti/condivisione/' + token + '/verifica-pin/', { pin }),
  // FE-074 — URL diretto di download (link vero, non window.open): funziona
  // anche nei webview mobile. Per i link con PIN si ripassa il PIN gia'
  // verificato; inline=true per la visualizzazione nel browser.
  downloadUrl: (token, pin, inline) => {
    var params = [];
    if (pin) params.push('pin=' + encodeURIComponent(pin));
    if (inline) params.push('inline=1');
    var qs = params.length ? '?' + params.join('&') : '';
    return BASE_URL + '/documenti/condivisione/' + token + '/download/' + qs;
  },
};

// Accesso esterno M4 — endpoint con JWT dedicato (non usa interceptor app)
function _esternoHeaders(token) { return { headers: { Authorization: 'Bearer ' + token } }; }

export const accessoEsternoPublic = {
  login: (email, password) => axios.post(BASE_URL + '/documenti/accesso-esterno/login/', { email, password }),
  cambiaPassword: (token, password_attuale, password_nuova) =>
    axios.post(BASE_URL + '/documenti/accesso-esterno/cambia-password/', { password_attuale, password_nuova }, _esternoHeaders(token)),
  cartelle: (token) => axios.get(BASE_URL + '/documenti/accesso-esterno/cartelle/', _esternoHeaders(token)),
  documentiCartella: (token, cartellaId) =>
    axios.get(BASE_URL + '/documenti/accesso-esterno/cartelle/' + cartellaId + '/documenti/', _esternoHeaders(token)),
  documento: (token, docId) =>
    axios.get(BASE_URL + '/documenti/accesso-esterno/documenti/' + docId + '/', _esternoHeaders(token)),
  download: (token, docId) =>
    axios.get(BASE_URL + '/documenti/accesso-esterno/documenti/' + docId + '/download/', _esternoHeaders(token)),
};

export const knowledgeBase = {
  list:   (params) => api.get('/templates/', { params }),
  upload: (formData) => api.post('/templates/', formData),
  delete: (id) => api.delete(`/templates/${id}/`),
};

export const promemoria = {
  list: (params) => api.get('/promemoria/', { params }),
  oggi: () => api.get('/promemoria/oggi/'),
  prossimi: () => api.get('/promemoria/prossimi/'),
  create: (data) => api.post('/promemoria/', data),
  completa: (id) => api.post(`/promemoria/${id}/completa/`),
};

export const ai = {
  tasks:   (params) => api.get('/ai/tasks/', { params }),
  task:    (id)     => api.get(`/ai/tasks/${id}/`),
  usage:   ()       => api.get('/ai/usage/'),
  rispondi: (id, risposta) => api.post(`/ai/task/${id}/rispondi/`, { risposta }),
};

export const billing = {
  me: () => api.get('/billing/me/'),
};

export const stripe = {
  // CAP-007 — listino dal catalogo Stripe (month/year per piano + addon collaboratore).
  prezzi: () => api.get('/stripe/prezzi/'),
};

export const news = {
  list: (params) => api.get('/news/', { params }),
  feed: (params) => api.get('/news/feed/', { params }),
  invia: (id, data) => api.post(`/news/feed/${id}/invia/`, data),
};

export const supporto = {
  unreadCount: () => api.get('/supporto/unread_count/'),
  list: (params) => api.get('/supporto/', { params }),
  create: (data) => api.post('/supporto/', data),
  detail: (id) => api.get(`/supporto/${id}/`),
  inviaMessaggio: (id, testo) => api.post(`/supporto/${id}/messaggi/`, { testo }),
  allegati: (id) => api.get(`/supporto/${id}/allegati/`),
  uploadAllegato: (id, formData) => api.post(`/supporto/${id}/allegati/`, formData),
  valutazione: (id, val) => api.patch(`/supporto/${id}/valutazione/`, { valutazione: val }),
};

export const cms = {
  contenuti: () => api.get('/cms/contenuti/'),
};

export const notifiche = {
  list: () => api.get('/notifiche/'),
};

export const guida = {
  struttura: () => api.get('/guida/'),
};
